import { stateOptions } from "data";
import { toYN } from "./helpers"; // import helpers above

// ---- helpers ----
const toMMDDYYYY = (d) => {
  if (!d) return "";
  if (typeof d === "string") {
    // Avoid timezone shifts when formatting plain date strings from the form
    const isoMatch = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, yyyy, mm, dd] = isoMatch;
      return `${mm}/${dd}/${yyyy}`;
    }
    const slashMatch = d.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashMatch) return d; // already in desired format
  }

  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";

  const useUTC =
    dt.getUTCHours() === 0 &&
    dt.getUTCMinutes() === 0 &&
    dt.getUTCSeconds() === 0;
  const mm = String((useUTC ? dt.getUTCMonth() : dt.getMonth()) + 1).padStart(2, "0");
  const dd = String(useUTC ? dt.getUTCDate() : dt.getDate()).padStart(2, "0");
  const yyyy = useUTC ? dt.getUTCFullYear() : dt.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const onlyDigits = (s) => (s ?? "").replace(/\D/g, "");
const sum = (arr) => arr.reduce((a, b) => a + b, 0);
const monthsBetween = (yearStr, monthStr) => {
  const y = Number(yearStr), m = Number(monthStr);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return 0;
  const start = new Date(y, m - 1, 1);
  const now = new Date();
  return (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
};

// map Step8 commodities -> legacy shape expected by DB
const mapCommodities = (list = []) =>
  list.map((c, i) => ({
    commodityId: i + 1,
    commodityKey: c.commodityKey,
    commodityPercentage: c.commodityPercentage,
    refrigerated: c.refrigerated ? "Y" : "N",
    generalMerchDetail: c.generalMerchDetail,
  }));

const mapStateMileage = (list = []) =>
  list.map((s, i) => ({
    state: s.state,
    percentage: s.percentage,
    miles: s.miles,
  }));

// map Step9 losses -> legacy 1/2/3 blocks
const mapLosses = (step9) => {
  const L = (i) => ({
    lossConfirmed: "Y",
    lossAlCount: step9?.losses?.[i]?.autoLiability?.count ?? 0,
    lossAlPaid:  step9?.losses?.[i]?.autoLiability?.paid ?? 0,
    lossApdCount: step9?.losses?.[i]?.physicalDamage?.count ?? 0,
    lossApdPaid:  step9?.losses?.[i]?.physicalDamage?.paid ?? 0,
    lossMtcCount: step9?.losses?.[i]?.cargo?.count ?? 0,
    lossMtcPaid:  step9?.losses?.[i]?.cargo?.paid ?? 0,
    lossTglCount: step9?.losses?.[i]?.tgl?.count ?? 0,
    lossTglPaid:  step9?.losses?.[i]?.tgl?.paid ?? 0,
    lossNtlCount: 0,
    lossNtlPaid: 0,
  });
  return { 1: L(0), 2: L(1), 3: L(2) };
};

export const mapData = (data, agency, user, location, showNotification) => {
  const carrier = data?.step1?.carrier || "Cover Whale";

  const mailing = data?.step2?.mailingAddress;
  const garaging = data?.step2?.garagingAddress;

  const vehicles = (data?.step6?.vehicles ?? []).map((v) => ({
    id: v.vehicleId ?? crypto.randomUUID?.() ?? String(Math.random()),
    bodyTypeKey: v.bodyType,
    classKey: v.class,
    deductible: "",
    deductibleType: "",
    make: v.make,
    model: v.model,
    value: v.value,
    vin: v.vin,
    year: String(v.year),
    includeAPDTowing: !!v.includeApdTowing,
    hitchType: v.hitchType,
    type: "vehicle",
  }));

  let trailers = (data?.step7?.trailers ?? []).map((t) => ({
    id: t.trailerId ?? crypto.randomUUID?.() ?? String(Math.random()),
    bodyTypeKey: t.bodyType,
    make: t.make,
    model: t.model,
    value: t.value,
    vin: t.vin,
    year: String(t.year),
    deductible: t.deductible ?? "",
    deductibleType: t.deductibleType ?? "",
    type: t.type ?? "trailer",
  }));

  const drivers = (data?.step5?.drivers ?? []).map((d) => ({
    id: d.driverId ?? crypto.randomUUID?.() ?? String(Math.random()),
    firstName: d.firstName,
    lastName: d.lastName,
    dateOfBirth: toMMDDYYYY(d.dateOfBirth),
    dateOfHire: d.dateOfHire ? toMMDDYYYY(d.dateOfHire) : null,
    yearsExperience: d.yearsExperience,
    licenseState: d.licenseState,
    licenseNumber: d.licenseNumber,
    accidents: String(d.accidents ?? 0),
    accidents_total: String(d.accidents ?? 0),
    violations: String(d.violations ?? 0),
    violations_total: String(d.violations ?? 0),
    suspensions: String(d.suspensions ?? 0),
    suspensions_total: String(d.suspensions ?? 0),
    eligibility: d.eligibility ?? "",
  }));

  const valueOfTrucks = sum(vehicles.map((v) => Number(v.value || 0)));
  const valueOfTrailers = sum(trailers.map((t) => Number(t.value || 0)));

  const stateLabel = (stateOptions.find((s) => s.value === agency?.state)?.label) ?? agency?.state ?? "";
  const retailAgent = {
    FirstName: (user?.data?.name || "").split(" ")[0] ?? "",
    LastName: (user?.data?.name || "").split(" ")[1] ?? "",
    Phone: onlyDigits(agency?.certificate_contacts?.[0]?.phone),
    Email: agency?.certificate_contacts?.[0]?.email ?? "",
    AgencyName: String(agency?.name ?? "").replaceAll(" ", ""),
    Street: `${agency?.address1 ?? ""} ${agency?.address2 ?? ""}`.trim(),
    City: agency?.city ?? "",
    State: stateLabel,
    Zip: agency?.zip ?? "",
  };

  const bd = data?.step3?.business_description ?? [];
  const has = (k) => bd.includes(k);

  const effectiveDateMMDDYYYY = toMMDDYYYY(data?.step1?.policyDate);

  let requestTgl = toYN(data?.step8?.truckers_general_liability);
  if (
    toYN(data?.step8?.auto_liability) === "Y" &&
    requestTgl === "N" &&
    toYN(data?.step8?.automobile_physical_damage) === "N" &&
    toYN(data?.step8?.motor_truck_cargo) === "N" &&
    toYN(data?.step8?.non_trucking_liability) === "N") {
    requestTgl = 'Y';
  }
  const documents = (data?.step10?.documents ?? []).map((doc) => ({
    type: doc?.type,
    url: doc?.url,
    name: doc?.name ?? "",
    id: doc?.id ?? undefined,
  })).filter((d) => d.type && d.url);

  if (has("dump_truck_sand_gravel")) {
    const shouldAddDumpTrailer = vehicles.length > 0 && (
      vehicles.some(vehicle =>
        vehicle.bodyTypeKey === "tractor" ||
        vehicle.bodytype === "tractor"
      )
    );
    if (shouldAddDumpTrailer) {
      const generateVin = () => {
        const prefix = "4YMBD1222RT";
        const randomDigits = Math.floor(100000 + Math.random() * 900000); // 6 random digits
        return `${prefix}${randomDigits}`;
      };

      const nonOwnedTrailer = {
        type: "trailer",
        vin: generateVin(),
        year: "2020",
        make: "Non-Owned",
        model: "NA",
        value: "5000",
        bodyTypeKey: "dump_trailer",
        loss_payee: "",
        company_id: location?.state?.data?.company_id
      };


      if (!trailers) {
        trailers = [];
      }

      trailers = [...trailers, nonOwnedTrailer];
      data.step7.nbrOfTrailers = trailers.length;

      data.valueOfTrailers = (Number(data.valueOfTrailers) || 0) + Number(nonOwnedTrailer.value);

      showNotification(
        'Non-Owned Dump Trailer automatically added to quote',
        'info',
        true
      );
    }
  }
  const commodityArray = data?.step8?.commodity_array ?? [];
  const includeCommodities = data?.step8?.motor_truck_cargo || commodityArray.length > 0;

  const postData = {
    agent_email: data?.step1?.agentEmail ?? "",
    dot_number: data?.step2?.dot?.dotNumber ?? null,
    effective_date: effectiveDateMMDDYYYY,
    uploaded_by: user?.data?.name ?? "",
    carrier,
    company_id: location?.state?.data?.company_id,
    number_of_drivers: drivers.length,
    number_of_vehicles: vehicles.length,
    number_of_trailers: trailers.length,
    premium: 0,
    quote: {
      producer: data?.step1?.producer ?? "",
      step1: {
        insuredWithProgressiveCommercialAuto: toYN(data?.step1?.insuredWithProgressiveCommercialAuto),
        hazmatPlacard: toYN(data?.step1?.hazmatPlacard),
        haulDoubleTripleTrailers: toYN(data?.step1?.haulDoubleTripleTrailers),
        operatedTwoYears: toYN(data?.step1?.operatedTwoYears),
        personalUseOfTrailer: toYN(data?.step1?.personalUseOfTrailer),
        garageState: data?.step1?.garageState ?? "",
      },
      coverage: {
        requestAl: toYN(data?.step8?.auto_liability),
        optAlPip: toYN(data?.step8?.personal_injury_protection),
        optAlUm: toYN(data?.step8?.uninsured_motorists_property_damage),
        requestApd: toYN(data?.step8?.automobile_physical_damage),
        requestMtc: toYN(data?.step8?.motor_truck_cargo),
        requestTgl: requestTgl,
        requestNtl: toYN(data?.step8?.non_trucking_liability),
        effectiveDate: effectiveDateMMDDYYYY,

        // yes/no enums → Y/N
        currentlyInsured: toYN(data?.step8?.currentlyInsured),
        continuousCoverage: toYN(data?.step8?.continuousCoverage),

        // booleans → Y/N
        compCollisionDeductibleChecked: toYN(data?.step8?.compCollisionDeductibleChecked),
        currentBILimitChecked: toYN(data?.step8?.currentBILimitChecked),
        hiredAutoLiability: toYN(data?.step8?.hiredAutoLiability),

        // "yes" literal when chosen → Y/N
        rentalReimbursement: toYN(data?.step8?.rentalReimbursement === "yes"),
        roadsideAssistance: toYN(data?.step8?.roadsideAssistance === "yes"),
        blanketAdditionalInsured: toYN(data?.step8?.blanketAdditionalInsured === "yes"),
        nonOwnedTrailerCoverage: toYN(data?.step8?.nonOwnedTrailerCoverage === "yes"),

        // plain values
        hiredAutoCount: data?.step8?.hiredAutoCount ?? undefined,
        compCollisionDeductibleOption: data?.step8?.compCollisionDeductibleOption ?? undefined,
        currentBILimitValue: data?.step8?.currentBILimitValue ?? undefined,

        mtc_deductible: data?.step8?.mtc_deductible ?? undefined,
        nonOwnedTrailersCount: data?.step8?.nonOwnedTrailersCount ?? undefined,
        nonOwnedTrailers: data?.step8?.nonOwnedTrailers ?? [],
      },
      insuredInformation: {
        owner: data?.step2?.owner,
        dotNumber: data?.step2?.dot?.dotNumber ?? null,
        motorCarrierNumber: data?.step2?.motorCarrierNumber ?? null,
        email: data?.step2?.insuredContact?.email ?? "",
        hasLossRuns: toYN(data?.step2?.hasLossRuns),
        insuredIssuedWithInLastSixtyDays: toYN(data?.step2?.insuredIssuedWithInLastSixtyDays),
        legalName: data?.step2?.insuredName ?? "",
        dbaName: data?.step2?.dba ?? "",
        yearsInBusiness: (() => {
          const months = monthsBetween(
            data?.step3?.operatingStart?.year,
            data?.step3?.operatingStart?.month
          );
          return Math.floor(months / 12);
        })(),
        monthsInBusiness: (() => {
          const months = monthsBetween(
            data?.step3?.operatingStart?.year,
            data?.step3?.operatingStart?.month
          );
          const remainder = months % 12;
          return remainder < 0 ? 0 : remainder;
        })(),
        insuranceContactFirstName: data?.step2?.insuredContact?.firstName ?? "",
        insuranceContactLastName: data?.step2?.insuredContact?.lastName ?? "",
        insuranceContactPhone: onlyDigits(data?.step2?.insuredContact?.phone),
        insuranceContactEmail: data?.step2?.insuredContact?.email ?? "",
        entityType: data?.step3?.entityType ?? "",
        fein: data?.step2?.fein ?? "",
        revenue: data?.step2?.revenue ?? undefined,
        miles: data?.step2?.miles ?? undefined
      },
      limits: {
        trailerInterchange: toYN(data?.step8?.trailer_interchange),
        trailerInterchangeLimit: data?.step8?.trailer_interchange_limit ?? "",
        limitTowingStorage: data?.step8?.limits?.towingLimit ?? "",
        nbrOfTrucks: vehicles.length,
        valueOfTrucks,
        nbrOfTrailers: (data?.step7?.nbrOfTrailers ?? trailers.length) || 0,
        valueOfTrailers,
        limitAutoLiability: data?.step8?.auto_liability_limit_for_cover_whale_and_nirvana,
        autoLiabilityLimitForProgressive: data?.step8?.auto_liability_limit_for_progressive,
        mtcLimit: data?.step8?.motor_truck_cargo ? (data?.step8?.motor_truck_cargo_limit ?? "0") : "0",

        insuredWaiverSubrogation: toYN(data?.step8?.limits?.waiverSubrogation),
        primaryNoncontributoryTGL: toYN(data?.step8?.limits?.primaryNoncontribTGL),
        primaryNoncontributoryAL: toYN(data?.step8?.limits?.primaryNoncontribAL),
      },
      operations: {
        operatingStart: data?.step3?.operatingStart,
        businessDescription: data?.step3?.business_description ?? [],
        operationDescription: data?.step3?.operationDescription ?? "",

        // legacy flags derived from business_description
        opsLocal: has("local_trucker") || has("logging") ? "Y" : "N",
        opsIntermediate: has("intermediate") ? "Y" : "N",
        opsLongHaul: has("long_haul_trucker") ? "Y" : "N",
        opsIntermodalPort: has("intermodal_or_port") ? "Y" : "N",
        opsDumpTruckOther: has("dump_truck") ? "Y" : "N",
        opsDumpTruckSandGravel: has("dump_truck_sand_gravel") ? "Y" : "N",
        opsEndDumper: has("end_dumper_units") ? "Y" : "N",
        opsLogging: has("logging") ? "Y" : "N",
        opsRefrigirated: has("refrigerated_trailer") ? "Y" : "N",
        opsHotShot: has("hot_shot") ? "Y" : "N",
        opsOversizedOvernight: has("oversized_or_overweight") ? "Y" : "N",
        opsAutomobileHauler: has("automobile_hauler") ? "Y" : "N",
        opsHouseholdGoods: has("household_goods_mover") ? "Y" : "N",
        opsTanker: has("tankers") ? "Y" : "N",

        // straight from Step3/Step1 → Y/N
        ineligibleOperations: toYN(data?.step3?.hasIneligibleOps) ?? "N",
        priorInsuranceCancelledNonrenewed: toYN(data?.step3?.priorCancelled) ?? "N",
        cancellations12mo: toYN(data?.step3?.cancellations12mo) ?? "N",
        fourOrMoreCancellationsInPreviousYear: "N",
        ...(() => {
          const carriers = Array.isArray(data?.step1?.carrier)
            ? data.step1.carrier
            : data?.step1?.carrier
            ? [data.step1.carrier]
            : [];
          const hasCW = carriers.some((c) => c === "Cover Whale");
          const hasNirvana = carriers.some((c) => c === "Nirvana");

          const step4 = data?.step4 ?? {};

          // ---- CW-facing fields (dataSharingOption + eldVendorName) ----
          // Requirement: eldVendorName must ALWAYS be null for Cover Whale.
          let dataSharingOption = step4.telemetryOption ?? "";
          let eldVendorName = null;
          let dashCamProviderText = null;

          // ---- Nirvana-facing fields (human-readable) ----
          let nirvanaDataSharingOption = null;
          let nirvanaDataSharingProvider = null;

          if (hasCW || hasNirvana) {
            if (hasNirvana) {
              nirvanaDataSharingOption =
                step4.telemetryOption === "eld_only"
                  ? "ELD"
                  : step4.telemetryOption === "dash_cam_dual_ai_connected"
                  ? "Dash Camera"
                  : null;
              nirvanaDataSharingProvider = step4.telemetryProvider ?? null;
            }

            if (step4.telemetryOption === "dash_cam_dual_ai_connected") {
              dashCamProviderText = step4.telemetryProvider ?? null;
            }
          }

          return {
            dataSharingOption,
            eldVendorName,
            dashCamProviderText,
            nirvanaDataSharingOption,
            nirvanaDataSharingProvider,
          };
        })(),
        bankruptcies: "N",

        personalUseOfVehicle: toYN(data?.step1?.personalUseOfVehicle) ?? "N",
        UIIAintermodal: has("uiia_intermodal") ? "Y" : (toYN(data?.step3?.uiiaIntermodal) ?? "N"),
        filingsAlFederal: toYN(data?.step3?.filingsAlFederal) ?? "N",
        filingsAlState: toYN(data?.step3?.filingsAlState) ?? "N",
        roadsideConstruction: toYN(data?.step3?.roadsideConstruction) ?? "N",
        dumpLoadsFullyTarped: toYN(data?.step3?.dumpLoadsTarped) ?? "N",
        driversMaintainSixInchesFreeBoard: toYN(data?.step3?.freeBoardSixInches) ?? "N",
        noneOfMentioned: toYN(data?.step3?.noneOfMentioned) ?? "N",
        oversizedShipments: toYN(data?.step3?.oversizedShipments) ?? "N",
        brokerageAuthority: toYN(data?.step3?.brokerageAuthority) ?? "N",
        ownerOperators: toYN(data?.step3?.ownerOperators) ?? "N",
        interlineAgreement: toYN(data?.step3?.interlineAgreement) ?? "N",
        interchangeAgreement: toYN(data?.step3?.interchangeAgreement) ?? "N",
        intermodalAgreement: toYN(data?.step3?.intermodalAgreement) ?? "N",
        leaseVehiclesTrailers: toYN(data?.step3?.leaseVehiclesTrailers) ?? "N",
        travelMexico: toYN(data?.step3?.travelMexico) ?? "N",
        travelCanada: toYN(data?.step3?.travelCanada) ?? "N",
        teamDrivers: toYN(data?.step3?.teamDrivers) ?? "N",
        guestPassengers: toYN(data?.step3?.guestPassengers) ?? "N",
        subsidiary: toYN(data?.step3?.subsidiary) ?? "N",
        tripLeasingUnderAuthority: toYN(data?.step3?.tripLeasingUnderAuthority) ?? "N",
        vehicleFilesDotCompliant: toYN(data?.step6?.vehicleFilesDotCompliant) ?? "Y",
      },
      step5: {
        carrier: data?.step5?.carrier ?? [],
        wayOfCompensation: data?.step5?.wayOfCompensation ?? null,

        companyRulesProcedures: toYN(data?.step5?.companyRulesProcedures) ?? "N",
        dailyVehicleInspections: toYN(data?.step5?.dailyVehicleInspections) ?? "N",
        equipmentFamiliarization: toYN(data?.step5?.equipmentFamiliarization) ?? "N",
        routeFamiliarization: toYN(data?.step5?.routeFamiliarization) ?? "N",
        emergencyProcedures: toYN(data?.step5?.emergencyProcedures) ?? "N",
        accidentReportingProcedures: toYN(
          data?.step5?.accidentReportingProcedures
        ) ?? "N",
        writtenDriverSafetyManual: toYN(
          data?.step5?.writtenDriverSafetyManual
        ) ?? "N",

        driverHomeDaysPerWeek:
          data?.step5?.driverHomeDaysPerWeek ?? null,
        driversDotCompliant: toYN(data?.step5?.driversDotCompliant) ?? "Y",
        minimumAgeOfDrivers: data?.step5?.minimumAgeOfDrivers ?? null,
        violationsPastSixMonths:
          data?.step5?.violationsPastSixMonths ?? null,
        zeroTolerancePolicyForMajorViolations: toYN(
          data?.step5?.zeroTolerancePolicyForMajorViolations
        ) ?? "N",

        disciplinaryActionForDrivers:
          data?.step5?.disciplinaryActionForDrivers ?? "",
        howOftenAreMandatorySafetyMeetingsHeld:
          data?.step5?.howOftenAreMandatorySafetyMeetingsHeld ?? "",

        maintainAccidentRegister: toYN(
          data?.step5?.maintainAccidentRegister
        ) ?? "N",
        writtenMaintenanceProgram: toYN(
          data?.step5?.writtenMaintenanceProgram
        ) ?? "N",
        insuredDoTheirOwnMaintenance: toYN(
          data?.step5?.insuredDoTheirOwnMaintenance
        ) ?? "N",
        safetyRewardsProgram: toYN(
          data?.step5?.safetyRewardsProgram
        ) ?? "N",
        mvrsCheckedAnnually: toYN(
          data?.step5?.mvrsCheckedAnnually
        ) ?? "N",

        cdlExperience: data?.step5?.cdlExperience ?? null,
      },
      garageAddress: {
        garageStreet: garaging?.address1 ?? "",
        garageStreet2: garaging?.address2 ?? "",
        garageCity: garaging?.city ?? "",
        garageState: garaging?.state ?? "",
        garageZip: garaging?.zip ?? "",
        garageCounty: "County",
        garageCountry: "USA",
      },
      mailingAddress: {
        mailingStreet: mailing?.address1 ?? "",
        mailingStreet2: mailing?.address2 ?? "",
        mailingCity: mailing?.city ?? "",
        mailingState: mailing?.state ?? "",
        mailingZip: mailing?.zip ?? "",
        mailingCounty: "County",
        mailingCountry: "USA",
      },
      garagingSameAsMailing: toYN(data?.step2?.garagingSameAsMailing),
      shippingAddress: {
        shippingStreet: mailing?.address1 ?? "",
        shippingCity: mailing?.city ?? "",
        shippingState: mailing?.state ?? "",
        shippingZip: mailing?.zip ?? "",
        shippingCounty: "County",
        shippingCountry: "USA",
      },
      radius: {
        radius0_50: data?.step8?.radius0_50 ?? "",
        radius51_200: data?.step8?.radius51_200 ?? "",
        radius201_500: data?.step8?.radius201_500 ?? "",
        radius501: data?.step8?.radius501 ?? "",
        stateOperation: data?.step8?.stateOperation ?? "",
        annualMileage: data?.step8?.annualMileage ?? "",
      },
      commoditiesRefrigeration: includeCommodities
        ? commodityArray.some((c) => c.refrigerated)
          ? "Y"
          : "N"
        : undefined,
      commodities: includeCommodities ? mapCommodities(commodityArray) : undefined,
      stateMileage: mapStateMileage(data?.step8?.stateMileage),
      vehicles,
      trailers,
      drivers,
      losses: mapLosses(data?.step9),
      retailAgent,
      step10: {
        documents,
        targetPricePerTruck: data?.step10?.targetPricePerTruck ?? "",
      },
    },
    documents,
  };

  const cleanData = JSON.parse(
    JSON.stringify(data, (key, value) => {
      if (value instanceof Date) return value.toISOString();
      if (typeof value === "function") return undefined;
      return value;
    })
  );

  postData.allData = {
    ...postData,
    ...cleanData,
  };


  return postData;
};
